import "./App.css";
import { Route, Routes, useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import MixedCodeResponse from "./MixedCodeResponse";
import ScrollToBottomButton from "./ScrollToBottomButton";
import AppStoreDownload from "./AppStoreDownload";
import ImageDisplay from "./ImageDisplay";
import "./ReloadButton.css";
import LoginModal from "./LoginModal";
import SignUpModal from "./SignUpModal";
import MyAccountModal from "./MyAccountModal";
import AppleCallback from "./AppleCallback";
import TextInputWithButton from "./TextInputWithButton";
import EmailVerificationResult from "./EmailVerificationResult";
import SubscriptionSuccess from './SubscriptionSuccess';
import SubscriptionCancel from './SubscriptionCancel';
import SubscriptionButton from './SubscriptionButton';
import axios from "axios";
import SubscriptionStatus from "./SubscriptionStatus";
import { jwtDecode } from "jwt-decode";

// Endpoints for authentication, etc.
const LOGIN_ENDPOINT = "https://developer-ai.com/devaix/dev_ai_login/login";
const SIGNUP_ENDPOINT = "https://developer-ai.com/devaix/dev_ai_login/signup";
const RESEND_VERIFICATION_ENDPOINT = "https://developer-ai.com/devaix/dev_ai_login/resend-verification";

const CODE_GEN_WEB_ENDPOINT = "https://developer-ai.com/devaix/o-code-gen-ai-web-1";
const MIRROR_SESSION_QR_ENDPOINT = "https://developer-ai.com/devaix/mirror-session-qr";
const SESSION_TEXT_FETCH_URL_PREFIX = "https://developer-ai.com/devaix/mirror-session-fetch?session_key=";
const AUTO_LOGIN_ENDPOINT = "https://developer-ai.com/devaix/dev_ai_login/auto-login";

const devaiappiourl = "https://apps.apple.com/us/app/developer-ai/id6502913351";
const devaiapplogourl = "https://yolleg.com/img_files/developer_ai_logo.png";
const stripeSubPriceId1 = 'price_1R4XGiLS1T1y5uFxQqrU4qU7';


function Header({ user, subscription, onManageSubscription, onLoginClick, onAccountClick }) {
  const buttonStyle = {
    borderRadius: "5px",
    backgroundColor: "#302782",
    color: "white",
    padding: "12px 32px",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    margin: "10px",
  };

  return (
    <div style={{ textAlign: "left", margin: "25px", display: 'flex', alignItems: 'center' }}>
      {user ? (
        <>
          <button onClick={onAccountClick} style={buttonStyle}>
            My Account
          </button>
          <SubscriptionButton
            priceId={stripeSubPriceId1}
            user={user}
            subscription={subscription}
            onManageSubscription={onManageSubscription}
          />
        </>
      ) : (
        <button onClick={onLoginClick} style={buttonStyle}>
          Login
        </button>
      )}
    </div>
  );
}

// Pass user and subscription props to AutoRefresh so it can decide whether to render the QR block.
const AutoRefresh = ({ user, subscription }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [lastGenerationPrompt, setLastGenerationPrompt] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [sessionKey, setSessionKey] = useState(null);

  useEffect(() => {
    if (user) return;

    setData(null);
    setQrData(null);
    setSessionKey(null);
    setSessionError(null);
    setGenerationError(null);
    setLastGenerationPrompt("");
  }, [user]);

  // Fetch the QR code data from the /mirror-session-qr endpoint.
  useEffect(() => {
    if (!user || !subscription) return; // Only fetch QR data when user and subscription are available
  
    const fetchQrData = async () => {
      try {
        const response = await fetch(MIRROR_SESSION_QR_ENDPOINT, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("accessToken")}`,
          },
        });
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const qrResult = await response.json();
        setQrData(qrResult);
        if (qrResult.qr_text) {
          setSessionKey(qrResult.qr_text);
        }
      } catch (e) {
        console.error("Error fetching QR data:", e);
      }
    };
  
    fetchQrData();
  }, [user, subscription]);
  

  // Fetch session text from the mirror-session-fetch endpoint using the latest sessionKey.
  useEffect(() => {
     // Skip if prerequisites are not met
    if (!user || !subscription || !sessionKey) {
      setLoading(false); // explicitly mark loading as done
      return;
    }
  
    const fetchData2 = async () => {
      try {
        let accessToken = localStorage.getItem("accessToken");
        const devurl2 = `${SESSION_TEXT_FETCH_URL_PREFIX}${encodeURIComponent(sessionKey)}`;
        const response = await fetch(devurl2, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${accessToken}`,
          },
        });
    
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
    
        const jsonResult = await response.json();
        const sessionText = jsonResult?.session_text || "";
        setData(prevData =>
          sessionText === prevData ? prevData : sessionText
        );
        setSessionError(null);
    
      } catch (error) {
        setSessionError(error.message);
      } finally {
        setLoading(false);
      }
    };
  
    // Immediately fetch and then set an interval
    fetchData2();
    const intervalId = setInterval(() => {
      fetchData2();
      console.log("Refreshed at:", new Date().toLocaleTimeString());
    }, 10000);
  
    return () => clearInterval(intervalId);
  }, [sessionKey, user, subscription]);
  
  const requestGeneration = async (text) => {
    const accessToken = localStorage.getItem("accessToken");
    setIsFetching(true);
    setGenerationError(null);
    try {
      const payload = {
        session_key: sessionKey,
        input_text: text,
      };
      const response = await fetch(CODE_GEN_WEB_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success || !result?.message) {
        throw new Error(
          result?.message || result?.error || `Generation failed (HTTP ${response.status}).`
        );
      }
      setData(result.message);
    } catch (e) {
      console.error("Error during POST fetch:", e);
      setGenerationError(e.message || "Couldn’t generate a response. Please try again.");
    } finally {
      setIsFetching(false);
    }
  };

  const handleTextSubmit = (text) => {
    const prompt = text.trim();
    if (!prompt || isFetching) return;
    setLastGenerationPrompt(prompt);
    requestGeneration(prompt);
  };

  const retryGeneration = () => {
    if (!lastGenerationPrompt || isFetching) return;
    requestGeneration(lastGenerationPrompt);
  };
  

  function LoadingIndicator() {
    return (
      <div
        style={{
          width: "24px",
          height: "24px",
          border: "4px solid #f3f3f3",
          borderTop: "4px solid #3498db",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "10px auto",
        }}
      ></div>
    );
  }

  return (
    <div>
      <div style={{ textAlign: "left", margin: "25px" }}>
        {loading && <p>Loading...</p>}
        {user && subscription && (
          <>
            {sessionKey ? (
              <TextInputWithButton onSubmit={handleTextSubmit} disabled={isFetching} />
            ) : (
              <p>Preparing your linked app session…</p>
            )}
            {isFetching && <LoadingIndicator />}
            {generationError && (
              <div role="alert" style={{ color: "#b42318", margin: "10px 0" }}>
                <p style={{ margin: "0 0 8px" }}>{generationError}</p>
                <button onClick={retryGeneration} disabled={isFetching || !lastGenerationPrompt}>
                  Retry
                </button>
              </div>
            )}
            {sessionError && (
              <p role="status" style={{ color: "#8a4b08" }}>
                Couldn’t refresh the linked app session: {sessionError}
              </p>
            )}
          </>
        )}
        {user && subscription && data !== null && (
          <>
            <hr style={{ border: "none", borderTop: "1px solid #ccc", margin: "10px 0" }} />
            <ScrollToBottomButton />
            <div className="code2">
              <pre>
                <MixedCodeResponse response={data} />
              </pre>
            </div>
          </>
        )}
      </div>
      <h1>Welcome to Developer-AI.com</h1>
      <br />
      <br />
      {/* Only render the QR code block if the user is logged in and has a subscription */}
      {user && subscription && qrData && (
        <>
          <h1>Scan this QR code in the Developer AI App</h1>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <img src={qrData.qr_image} alt="QR Code" />
          </div>
        </>
      )}
      <div style={{ display: "flex", gap: "20px", justifyContent: "center", alignItems: "center", marginBottom: "20px" }}>
        <a href={devaiappiourl}>
          <ImageDisplay imgURL={devaiapplogourl} />
        </a>
        <AppStoreDownload appUrl={devaiappiourl} />
      </div>
    </div>
  );
};

// Modify Home to accept and pass down user and subscription props.
function Home({ user, subscription }) {
  return <AutoRefresh user={user} subscription={subscription} />;
}

function App() {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);

  const navigate = useNavigate();
  const refreshTimerRef = useRef(null);
  const currentUserId = user?.id ?? null;

  const handleLogout = () => {
    setUser(null);
    setSubscription(null);
    setSubscriptionError(null);
    localStorage.removeItem("userEmail");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userId");
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    setShowAccountModal(false);
  };

  const scheduleTokenRefresh = (accessToken) => {
    try {
      const decoded = jwtDecode(accessToken);
      const expiryTime = decoded.exp * 1000;
      const currentTime = Date.now();
      const delay = expiryTime - currentTime - 60000;
      if (delay > 0) {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => { refreshAccessToken(); }, delay);
        console.log("Scheduled token refresh in", delay / 1000, "seconds");
      }
    } catch (error) {
      console.error("Failed to schedule token refresh:", error);
    }
  };

  const verifyToken = async (token) => {
    try {
      const response = await fetch("https://developer-ai.com/devaix/dev_ai_login/verify-token", {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        console.log("Token verified by server.");
        return true;
      } else {
        console.log("Server verification failed. Refreshing token.");
        await refreshAccessToken();
        return false;
      }
    } catch (error) {
      console.error("Error verifying token:", error);
      return false;
    }
  };

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    try {
      const response = await fetch("https://developer-ai.com/devaix/dev_ai_login/refresh-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      const result = await response.json();
      if (result.success) {
        localStorage.setItem("accessToken", result.access_token);
        scheduleTokenRefresh(result.access_token);
        return result.access_token;
      } else {
        handleLogout();
        return null;
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      handleLogout();
      return null;
    }
  };

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        const valid = await verifyToken(token);
        if (!valid) await refreshAccessToken();
        else {
          try {
            const decoded = jwtDecode(token);
            const expiryTime = decoded.exp * 1000;
            const currentTime = Date.now();
            if (expiryTime < currentTime + 60000) await refreshAccessToken();
            else scheduleTokenRefresh(token);
          } catch (error) {
            console.error("Error decoding token:", error);
            await refreshAccessToken();
          }
        }
      }
    };
    checkToken();
  }, []);

  useEffect(() => {
    const savedEmail = localStorage.getItem("userEmail");
    const savedUserId = localStorage.getItem("userId");
    if (savedEmail && savedUserId) {
      setUser({ email: savedEmail, id: savedUserId });
    }
  }, []);
  
  const handleEmailLogin = async (email, password) => {
    try {
      const response = await fetch(LOGIN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
  
      const result = await response.json();
      if (result.success && result.is_verified) {
        localStorage.setItem("accessToken", result.access_token);
        localStorage.setItem("refreshToken", result.refresh_token);
        localStorage.setItem("userEmail", email);
        localStorage.setItem("userId", result.user_id);
  
        setUser({ email, id: result.user_id, is_verified: result.is_verified });
        scheduleTokenRefresh(result.access_token);
        setShowLoginModal(false);
        return result;
      } else {
        return result;
      }
    } catch (error) {
      alert("Login error: " + error.toString());
      return { success: false, error: error.toString() };
    }
  };
  
  const fetchProtectedData = async (url) => {
    let accessToken = localStorage.getItem("accessToken");
    try {
      let response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
      });
      
      if (response.status === 401) {
        await refreshAccessToken();
        accessToken = localStorage.getItem("accessToken");
        response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`,
          },
        });
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching protected data:", error);
      return null;
    }
  };

  const handleEmailSignUp = async (email, password) => {
    try {
      const response = await fetch(SIGNUP_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const result = await response.json();
      if (result.success) return { success: true };
      else return { success: false, error: result.error };
    } catch (error) {
      return { success: false, error: error.toString() };
    }
  };
  
  const handleAppleLogin = () => {
    if (window.AppleID && window.AppleID.auth) {
      window.AppleID.auth.signIn();
    } else {
      console.error("Apple Sign-In is not available.");
      alert("Apple Sign-In is currently unavailable. Please use an alternative login method.");
    }
  };

  useEffect(() => {
    async function fetchSubscription() {
      setSubscriptionLoading(true);
      const userId = currentUserId;
      const accessToken = localStorage.getItem("accessToken");
      if (!userId || !accessToken) {
        setSubscription(null);
        setSubscriptionLoading(false);
        return;
      }
      try {
        const response = await fetch(
          `https://developer-ai.com/devaix/api/users/${userId}/subscription`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setSubscription(data);
          setSubscriptionError(null);
        } else {
          console.error("Failed to fetch subscription status");
        }
      } catch (error) {
        console.error("Error fetching subscription status:", error);
        setSubscriptionError(error.message);
      } finally {
        setSubscriptionLoading(false);
      }
    }
    fetchSubscription();
    const intervalId = setInterval(fetchSubscription, 30000);
    return () => clearInterval(intervalId);
  }, [currentUserId]);

  const handleManageSubscription = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(
        `${backendUrl}/api/create-customer-portal-session`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      const portalUrl = response.data.url;
      if (portalUrl) window.location.href = portalUrl;
      else alert("Unable to retrieve the subscription management portal.");
    } catch (error) {
      console.error("Error creating customer portal session:", error);
      alert("There was an error managing your subscription. Please try again later.");
    }
  };

  const handleAutoLogin = async (autoLoginToken) => {
    try {
      const response = await fetch(AUTO_LOGIN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ auto_login_token: autoLoginToken }),
      });
      const data = await response.json();
      if (data.success) {
        localStorage.setItem("accessToken", data.access_token);
        localStorage.setItem("refreshToken", data.refresh_token);
        localStorage.setItem("userEmail", data.email);
        localStorage.setItem("userId", data.user_id);
        setUser({ email: data.email, id: data.user_id });
        console.log("Auto login successful!");
        navigate("/");
      } else {
        console.error("Auto login failed:", data.error);
      }
    } catch (error) {
      console.error("Auto login error:", error);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const autoLoginToken = params.get("auto_login_token");
    if (autoLoginToken) {
      handleAutoLogin(autoLoginToken);
    }
  }, []);

  return (
    <div className="App">
      <Header
        user={user}
        subscription={subscription}
        onManageSubscription={handleManageSubscription}
        onLoginClick={() => setShowLoginModal(true)}
        onAccountClick={() => setShowAccountModal(true)}
      />
      {user && (
        <div style={{ margin: "20px", textAlign: "left" }}>
          <SubscriptionStatus
            subscription={subscription}
            loading={subscriptionLoading}
            error={subscriptionError}
          />
        </div>
      )}
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onEmailLogin={handleEmailLogin}
          onAppleLogin={handleAppleLogin}
          onSwitchToSignUp={() => {
            setShowLoginModal(false);
            setShowSignUpModal(true);
          }}
          RESEND_VERIFICATION_ENDPOINT={RESEND_VERIFICATION_ENDPOINT}
        />
      )}
      {showSignUpModal && (
        <SignUpModal
          onClose={() => setShowSignUpModal(false)}
          onEmailSignUp={handleEmailSignUp}
          onSwitchToLogin={() => {
            setShowSignUpModal(false);
            setShowLoginModal(true);
          }}
          onAppleLogin={handleAppleLogin}
          onEmailLogin={handleEmailLogin}
          RESEND_VERIFICATION_ENDPOINT={RESEND_VERIFICATION_ENDPOINT}
        />
      )}
      {showAccountModal && user && (
        <MyAccountModal
          user={user}
          onClose={() => setShowAccountModal(false)}
          onLogout={handleLogout}
        />
      )}
      <Routes>
        <Route path="/" element={<Home user={user} subscription={subscription} />} />
        <Route path="/apple-callback" element={<AppleCallback />} />
        <Route path="/email-verification-result" element={<EmailVerificationResult />} />
        <Route path="/subscription-success" element={<SubscriptionSuccess />} />
        <Route path="/subscription-cancel" element={<SubscriptionCancel />} />
      </Routes>
    </div>
  );
}

export default App;
